import { useEffect, useState, type FormEvent } from 'react';
import { useT } from '../../i18n';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid2 as Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { createInvoice } from '../../lib/invoices-api';
import { listAdminUnits, formatUnitOption, type AdminUnit } from '../../lib/units-api';
import { extractApiError } from '../../lib/api-client';

interface FormState {
  unit: AdminUnit | null;
  description: string;
  amountRupiah: string;
  taxRupiah: string;
  dueDate: string;        // YYYY-MM-DD (from <input type="date">)
  issueDate: string;      // YYYY-MM-DD; "" → omit from request → server defaults to today
  notes: string;
}

const todayIsoDate = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const defaultDueDate = (): string => {
  // Default = 14 days out, matches Indonesian IPL norms.
  const d = new Date();
  d.setDate(d.getDate() + 14);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export function InvoiceCreatePage() {
  const t = useT();
  const navigate = useNavigate();

  const [units, setUnits] = useState<AdminUnit[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [unitsError, setUnitsError] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>({
    unit: null,
    description: '',
    amountRupiah: '',
    taxRupiah: '',
    dueDate: defaultDueDate(),
    issueDate: todayIsoDate(),
    notes: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setUnitsLoading(true);
      setUnitsError(null);
      try {
        const list = await listAdminUnits();
        if (!cancelled) setUnits(list);
      } catch (e) {
        if (!cancelled) setUnitsError(extractApiError(e).message);
      } finally {
        if (!cancelled) setUnitsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    if (!form.unit) {
      setSubmitError(t('inv.createValidUnit'));
      return;
    }
    const description = form.description.trim();
    if (description.length === 0) {
      setSubmitError(t('inv.createValidDesc'));
      return;
    }
    const amount = Number(form.amountRupiah);
    if (!Number.isFinite(amount) || amount <= 0) {
      setSubmitError(t('inv.amountPositive'));
      return;
    }
    if (!form.dueDate) {
      setSubmitError(t('inv.createValidDueDate'));
      return;
    }
    let taxCents: number | undefined;
    if (form.taxRupiah.trim() !== '') {
      const taxNum = Number(form.taxRupiah);
      if (!Number.isFinite(taxNum) || taxNum < 0) {
        setSubmitError(t('inv.createValidTax'));
        return;
      }
      taxCents = Math.round(taxNum * 100);
    }

    setSubmitting(true);
    try {
      const inv = await createInvoice({
        unit_id: form.unit.id,
        description,
        amount_cents: Math.round(amount * 100),
        due_date: form.dueDate,
        ...(form.issueDate ? { issue_date: form.issueDate } : {}),
        ...(taxCents !== undefined ? { tax_cents: taxCents } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      });
      navigate(`/invoices/${inv.id}`);
    } catch (e) {
      setSubmitError(extractApiError(e).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/invoices"
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('inv.backToList')}
      </Button>

      <div>
        <Typography variant="h4" fontWeight={600}>
          {t('inv.createTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('inv.createSubtitle')}
        </Typography>
      </div>

      {unitsError ? <Alert severity="error">{t('inv.createUnitsError', { error: unitsError })}</Alert> : null}
      {submitError ? <Alert severity="error">{submitError}</Alert> : null}

      <Card variant="outlined">
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Autocomplete
                  options={units}
                  loading={unitsLoading}
                  value={form.unit}
                  onChange={(_, value) => update('unit', value)}
                  getOptionLabel={(opt) => formatUnitOption(opt)}
                  isOptionEqualToValue={(opt, value) => opt.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('inv.createFieldUnit')}
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {unitsLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label={t('inv.createFieldDesc')}
                  required
                  fullWidth
                  value={form.description}
                  onChange={(e) => update('description', e.target.value)}
                  helperText={t('inv.createDescHelper')}
                  inputProps={{ maxLength: 200 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('inv.amount')}
                  required
                  type="number"
                  fullWidth
                  value={form.amountRupiah}
                  onChange={(e) => update('amountRupiah', e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
                  }}
                  inputProps={{ min: 1, step: 1 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('inv.createFieldTax')}
                  type="number"
                  fullWidth
                  value={form.taxRupiah}
                  onChange={(e) => update('taxRupiah', e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 1 }}
                  helperText={t('inv.createTaxHelper')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('inv.createFieldIssueDate')}
                  type="date"
                  fullWidth
                  value={form.issueDate}
                  onChange={(e) => update('issueDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('inv.createFieldDueDate')}
                  type="date"
                  required
                  fullWidth
                  value={form.dueDate}
                  onChange={(e) => update('dueDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label={t('inv.createFieldNotes')}
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.notes}
                  onChange={(e) => update('notes', e.target.value)}
                  inputProps={{ maxLength: 500 }}
                  helperText={t('inv.createNotesHelper')}
                />
              </Grid>
              <Grid size={12}>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button component={RouterLink} to="/invoices" disabled={submitting}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" disabled={submitting || unitsLoading}>
                    {submitting ? t('inv.creating') : t('inv.createSubmit')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
}
